import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  ShoppingBag, Layers, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Search, Download, RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown,
  ArrowLeft, Calendar, Star, Zap, AlertTriangle, BarChart2, PieChart,
  Package, Tag, Info, ChevronRight, ChevronLeft, Filter,
} from "lucide-react";
import { CSVLink } from "react-csv";
import api from "../../utils/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Title, Tooltip, Legend, Filler);

/* ─── palette ──────────────────────────────────────────────────────────── */
const PALETTE = [
  "#6366f1","#14b8a6","#f59e0b","#ef4444","#8b5cf6",
  "#ec4899","#10b981","#f97316","#06b6d4","#84cc16",
];

const fmt  = (n) => (n||0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtK = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : n >= 1000 ? `₹${(n/1000).toFixed(1)}K` : `₹${fmt(n)}`;

const GrowthBadge = ({ v, inverse = false }) => {
  if (v === null || v === undefined) return <span className="text-gray-400 text-xs">—</span>;
  const up = v >= 0;
  const good = inverse ? !up : up;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${good ? "text-emerald-600" : "text-rose-500"}`}>
      {up ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}
      {up ? "+" : ""}{v}%
    </span>
  );
};

const TABS = [
  { key: "overview",    label: "Overview",    icon: BarChart2 },
  { key: "categories",  label: "Categories",  icon: Layers },
  { key: "products",    label: "Products",    icon: ShoppingBag },
  { key: "comparison",  label: "Comparison",  icon: TrendingUp },
];

const FILTERS = [
  { id: "today",     label: "Today" },
  { id: "7days",     label: "7 Days" },
  { id: "30days",    label: "30 Days" },
  { id: "thismonth", label: "This Month" },
  { id: "custom",    label: "Custom" },
];

/* ─── Component ─────────────────────────────────────────────────────────── */
const ProductAnalytics = () => {
  const [filter, setFilter]         = useState("7days");
  const [fromDate, setFromDate]     = useState("");
  const [toDate, setToDate]         = useState("");
  const [loading, setLoading]       = useState(true);
  const [data, setData]             = useState(null);
  const [error, setError]           = useState(null);
  const [activeTab, setActiveTab]   = useState("overview");

  /* products tab state */
  const [search, setSearch]         = useState("");
  const [catFilter, setCatFilter]   = useState("all");
  const [sortKey, setSortKey]       = useState("revenue");
  const [sortDir, setSortDir]       = useState("desc");
  const [page, setPage]             = useState(1);
  const PAGE = 15;

  /* categories tab */
  const [expandedCat, setExpandedCat] = useState(null);

  /* comparison chart view */
  const [cmpMetric, setCmpMetric] = useState("revenue"); // revenue | qty

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { filter };
      if (filter === "custom") {
        if (!fromDate || !toDate) { setLoading(false); return; }
        params.fromDate = fromDate;
        params.toDate   = toDate;
      }
      const res = await api.get("/analytics/product-stats", { params });
      if (res.data.success) setData(res.data.data);
      else setError(res.data.message || "Failed to load");
    } catch { setError("Failed to fetch product analytics"); }
    finally   { setLoading(false); }
  }, [filter, fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── derived ── */
  const categories = data?.categories || [];
  const products   = data?.products   || [];
  const catProdMap = data?.categoryProductMatrix || {};

  const catNames = useMemo(() => [...new Set(products.map(p => p.category))].sort(), [products]);

  const filteredProducts = useMemo(() => {
    let list = [...products];
    if (catFilter !== "all")  list = list.filter(p => p.category === catFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [products, catFilter, search, sortKey, sortDir]);

  const totalPages = Math.ceil(filteredProducts.length / PAGE);
  const pageProducts = filteredProducts.slice((page - 1) * PAGE, page * PAGE);

  const topProduct   = products[0];
  const topCategory  = categories[0];
  const totalQty     = products.reduce((s, p) => s + p.qty, 0);
  const uniqueProds  = products.length;

  /* ── charts ── */
  const catDonutData = {
    labels: categories.map(c => c.name),
    datasets: [{ data: categories.map(c => c.revenue), backgroundColor: PALETTE, borderColor: "#fff", borderWidth: 2 }],
  };
  const donutOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ₹${fmt(ctx.parsed)} (${categories[ctx.dataIndex]?.revenueShare}%)` } } } };

  const top5 = products.slice(0, 5);
  const barData = {
    labels: top5.map(p => p.name.length > 18 ? p.name.slice(0, 18) + "…" : p.name),
    datasets: [
      { label: "Revenue (₹)", data: top5.map(p => p.revenue), backgroundColor: PALETTE.slice(0, 5).map(c => c + "CC"), borderRadius: 6 },
    ],
  };
  const barOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ₹${fmt(ctx.parsed.y)}` } } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 10 } } }, y: { grid: { color: "#f3f4f6" }, ticks: { callback: v => fmtK(v) } } },
  };

  /* comparison bar */
  const cmpData = useMemo(() => {
    const top8 = [...categories].sort((a, b) => b[cmpMetric] - a[cmpMetric]).slice(0, 8);
    return {
      labels: top8.map(c => c.name),
      datasets: [
        { label: "Current Period", data: top8.map(c => c[cmpMetric]), backgroundColor: "rgba(99,102,241,0.8)", borderRadius: 6 },
        { label: "Prior Period",   data: top8.map(c => c[`prior${cmpMetric.charAt(0).toUpperCase()+cmpMetric.slice(1)}`] || 0), backgroundColor: "rgba(156,163,175,0.6)", borderRadius: 6 },
      ],
    };
  }, [categories, cmpMetric]);

  const cmpBarOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: "top", labels: { usePointStyle: true, font: { size: 11 } } },
      tooltip: { callbacks: { label: ctx => ` ${cmpMetric === "revenue" ? "₹" + fmt(ctx.parsed.y) : ctx.parsed.y + " units"}` } }
    },
    scales: { x: { grid: { display: false } }, y: { grid: { color: "#f3f4f6" }, ticks: { callback: v => cmpMetric === "revenue" ? fmtK(v) : v } } },
  };

  /* product comparison bar */
  const prodCmpData = useMemo(() => {
    const top8p = [...products].sort((a, b) => b[cmpMetric] - a[cmpMetric]).slice(0, 8);
    return {
      labels: top8p.map(p => p.name.length > 14 ? p.name.slice(0, 14) + "…" : p.name),
      datasets: [
        { label: "Current", data: top8p.map(p => p[cmpMetric]), backgroundColor: "rgba(20,184,166,0.8)", borderRadius: 6 },
        { label: "Prior",   data: top8p.map(p => cmpMetric === "revenue" ? p.priorRevenue : p.priorQty), backgroundColor: "rgba(156,163,175,0.6)", borderRadius: 6 },
      ],
    };
  }, [products, cmpMetric]);

  /* sort helper */
  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-gray-400 ml-0.5 inline"/>;
    return sortDir === "asc" ? <ChevronUp size={12} className="text-indigo-500 ml-0.5 inline"/> : <ChevronDown size={12} className="text-indigo-500 ml-0.5 inline"/>;
  };

  /* CSV data */
  const csvProducts = products.map((p, i) => ({
    Rank: i + 1, Product: p.name, Category: p.category,
    "Qty Sold": p.qty, "Revenue (₹)": p.revenue, "Avg Price (₹)": p.avgPrice,
    "Bills Appeared": p.billsAppeared, "Bill Frequency (%)": p.billFreqPct,
    "Revenue Share (%)": p.revenueShare,
    "Prior Qty": p.priorQty, "Prior Revenue (₹)": p.priorRevenue,
    "Qty Growth (%)": p.qtyGrowth, "Revenue Growth (%)": p.revenueGrowth,
  }));

  /* ── Loading / Error ── */
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center pt-20">
      <div className="text-center">
        <div className="w-14 h-14 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"/>
        <p className="text-gray-500 font-medium text-sm">Loading product intelligence...</p>
      </div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center pt-20 px-4">
      <div className="max-w-sm w-full bg-white p-8 rounded-2xl shadow-sm border text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3"/>
        <h2 className="text-lg font-bold text-gray-900 mb-2">Failed to load</h2>
        <p className="text-gray-500 text-sm mb-5">{error || "No data returned"}</p>
        <button onClick={fetchData} className="w-full py-2.5 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition text-sm">Retry</button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16 px-4 sm:px-6 lg:px-8">

      {/* ── Header ── */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => window.location.hash = "/analytics"}
              className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition shadow-sm text-gray-500">
              <ArrowLeft size={16}/>
            </button>
            <div>
              <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
                <ShoppingBag className="text-indigo-600" size={24}/>Product & Category Analytics
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Detailed performance breakdown by product and category
                {data.timeframe && <span className="ml-2 text-gray-300">|</span>}
                {data.timeframe && <span className="ml-2">{new Date(data.timeframe.startDate).toLocaleDateString("en-IN")} – {new Date(data.timeframe.endDate).toLocaleDateString("en-IN")}</span>}
              </p>
            </div>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* Filter pills */}
            <div className="bg-white border border-gray-200 p-1 rounded-xl flex gap-1 shadow-sm">
              {FILTERS.map(f => (
                <button key={f.id} onClick={() => { setLoading(true); setFilter(f.id); }}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${filter === f.id ? "bg-indigo-600 text-white shadow-sm" : "text-gray-500 hover:bg-gray-100"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <button onClick={fetchData} className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm" title="Refresh"><RefreshCw size={15}/></button>
            <CSVLink data={csvProducts} filename={`product-analytics-${filter}.csv`}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-sm transition">
              <Download size={14}/>Export
            </CSVLink>
          </div>
        </div>

        {/* Custom date */}
        {filter === "custom" && (
          <div className="mt-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-wrap items-end gap-4">
            {[["From", fromDate, setFromDate], ["To", toDate, setToDate]].map(([label, val, set]) => (
              <div key={label}>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">{label}</label>
                <input type="date" value={val} onChange={e => set(e.target.value)} max={new Date().toISOString().split("T")[0]}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
              </div>
            ))}
            <button onClick={fetchData} className="px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-bold hover:bg-gray-800 transition">Apply</button>
          </div>
        )}
      </div>

      {/* ── KPI Strip ── */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Products Sold", value: uniqueProds, sub: "Unique items", icon: <Package size={16} className="text-indigo-600"/>, bg: "bg-indigo-50" },
          { label: "Total Units Sold",    value: fmt(totalQty), sub: "Across all products", icon: <Zap size={16} className="text-teal-600"/>, bg: "bg-teal-50" },
          { label: "Best Seller",         value: topProduct?.name?.substring(0, 20) || "—", sub: `₹${fmt(topProduct?.revenue)} revenue`, icon: <Star size={16} className="text-amber-600"/>, bg: "bg-amber-50", small: true },
          { label: "Top Category",        value: topCategory?.name?.substring(0, 20) || "—", sub: `${topCategory?.revenueShare}% of revenue`, icon: <Layers size={16} className="text-purple-600"/>, bg: "bg-purple-50", small: true },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 font-semibold">{c.label}</span>
              <div className={`p-1.5 rounded-xl ${c.bg}`}>{c.icon}</div>
            </div>
            <p className={`font-extrabold text-gray-900 ${c.small ? "text-base" : "text-2xl"} truncate`}>{c.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition whitespace-nowrap ${activeTab === key ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
              <Icon size={15}/>{label}
            </button>
          ))}
        </div>

        <div className="p-5 lg:p-6">

          {/* ════════════════════════════════════════════════════════════ */}
          {/* OVERVIEW                                                     */}
          {/* ════════════════════════════════════════════════════════════ */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Category donut */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2"><PieChart size={15} className="text-indigo-600"/>Revenue by Category</h3>
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="h-44 w-44 flex-shrink-0">
                      {categories.length ? <Doughnut data={catDonutData} options={donutOpts}/> : <div className="h-full flex items-center justify-center text-gray-300 text-sm">No data</div>}
                    </div>
                    <div className="flex-1 space-y-2 w-full">
                      {categories.slice(0, 7).map((c, i) => (
                        <div key={c.name}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="flex items-center gap-1.5 font-medium text-gray-700">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PALETTE[i] }}/>
                              {c.name}
                            </span>
                            <span className="font-semibold text-gray-900">₹{fmt(c.revenue)} <span className="text-gray-400 font-normal">({c.revenueShare}%)</span></span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full">
                            <div className="h-1.5 rounded-full transition-all" style={{ width: `${c.revenueShare}%`, backgroundColor: PALETTE[i] }}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Top-5 products bar */}
                <div>
                  <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2"><BarChart2 size={15} className="text-teal-600"/>Top 5 Products by Revenue</h3>
                  <div className="h-52">
                    {products.length ? <Bar data={barData} options={barOpts}/> : <div className="h-full flex items-center justify-center text-gray-300 text-sm">No data</div>}
                  </div>
                </div>
              </div>

              {/* Summary table */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2"><Star size={15} className="text-amber-500"/>Top Performers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Top by revenue */}
                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    <div className="bg-indigo-50 px-4 py-2.5 text-xs font-black text-indigo-700 uppercase tracking-wider">Top by Revenue</div>
                    <table className="min-w-full">
                      <tbody className="divide-y divide-gray-50">
                        {products.slice(0, 5).map((p, i) => (
                          <tr key={p.name} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 text-sm">
                              <div className="flex items-center gap-2">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-black text-white ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-indigo-100 !text-indigo-600"}`}>{i + 1}</span>
                                <span className="font-semibold text-gray-800">{p.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{p.category}</td>
                            <td className="px-4 py-2.5 text-right text-sm font-bold text-indigo-600">₹{fmt(p.revenue)}</td>
                            <td className="px-4 py-2.5 text-right"><GrowthBadge v={p.revenueGrowth}/></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Top by qty */}
                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    <div className="bg-teal-50 px-4 py-2.5 text-xs font-black text-teal-700 uppercase tracking-wider">Top by Units Sold</div>
                    <table className="min-w-full">
                      <tbody className="divide-y divide-gray-50">
                        {[...products].sort((a, b) => b.qty - a.qty).slice(0, 5).map((p, i) => (
                          <tr key={p.name} className="hover:bg-gray-50/50">
                            <td className="px-4 py-2.5 text-sm">
                              <div className="flex items-center gap-2">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-black text-white ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-gray-400" : i === 2 ? "bg-orange-400" : "bg-teal-100 !text-teal-600"}`}>{i + 1}</span>
                                <span className="font-semibold text-gray-800">{p.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-gray-400">{p.category}</td>
                            <td className="px-4 py-2.5 text-right text-sm font-bold text-teal-600">{fmt(p.qty)} units</td>
                            <td className="px-4 py-2.5 text-right"><GrowthBadge v={p.qtyGrowth}/></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Bill frequency insight */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2"><Info size={15} className="text-blue-500"/>Bill Frequency (appears in % of bills)</h3>
                <div className="space-y-2">
                  {[...products].sort((a, b) => b.billFreqPct - a.billFreqPct).slice(0, 6).map((p, i) => (
                    <div key={p.name} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-32 truncate">{p.name}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full">
                        <div className="h-2 rounded-full transition-all" style={{ width: `${p.billFreqPct}%`, backgroundColor: PALETTE[i % PALETTE.length] }}/>
                      </div>
                      <span className="text-xs font-bold text-gray-700 w-12 text-right">{p.billFreqPct}%</span>
                      <span className="text-xs text-gray-400 w-20 text-right">{p.billsAppeared} bills</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* CATEGORIES                                                   */}
          {/* ════════════════════════════════════════════════════════════ */}
          {activeTab === "categories" && (
            <div className="space-y-3">
              {categories.length === 0 && (
                <div className="py-16 text-center text-gray-300">
                  <Layers size={40} className="mx-auto mb-3 opacity-50"/>
                  <p>No category data for this period</p>
                </div>
              )}
              {categories.map((cat, i) => (
                <div key={cat.name} className="rounded-xl border border-gray-100 overflow-hidden">
                  {/* Category header row */}
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition text-left"
                    onClick={() => setExpandedCat(expandedCat === cat.name ? null : cat.name)}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PALETTE[i % PALETTE.length] }}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-gray-900">{cat.name}</span>
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full text-xs font-bold">{cat.productCount} items</span>
                        {cat.peakHour !== null && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded-full text-xs font-medium">Peak: {cat.peakHour}:00</span>}
                      </div>
                      {/* mini progress bar */}
                      <div className="h-1 bg-gray-100 rounded-full w-48">
                        <div className="h-1 rounded-full" style={{ width: `${cat.revenueShare}%`, backgroundColor: PALETTE[i % PALETTE.length] }}/>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-6 text-right flex-shrink-0">
                      <div>
                        <p className="text-xs text-gray-400">Revenue</p>
                        <p className="font-bold text-gray-900 text-sm">₹{fmt(cat.revenue)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Units</p>
                        <p className="font-bold text-gray-900 text-sm">{fmt(cat.qty)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Bills</p>
                        <p className="font-bold text-gray-900 text-sm">{fmt(cat.billCount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">vs Prior</p>
                        <GrowthBadge v={cat.revenueGrowth}/>
                      </div>
                      <div className="text-xs font-bold text-gray-400">{cat.revenueShare}%</div>
                    </div>
                    <ChevronRight size={16} className={`text-gray-400 transition-transform flex-shrink-0 ${expandedCat === cat.name ? "rotate-90" : ""}`}/>
                  </button>

                  {/* Expanded: products in this category */}
                  {expandedCat === cat.name && (
                    <div className="border-t border-gray-100">
                      <div className="px-5 py-3 bg-gray-50 grid grid-cols-4 text-xs font-black text-gray-400 uppercase tracking-wider">
                        <span>Product</span>
                        <span className="text-right">Revenue</span>
                        <span className="text-right">Units</span>
                        <span className="text-right">Growth</span>
                      </div>
                      {(catProdMap[cat.name] || []).slice(0, 12).map(p => {
                        const full = products.find(x => x.name === p.name);
                        return (
                          <div key={p.name} className="px-5 py-3 grid grid-cols-4 items-center hover:bg-white/60 border-t border-gray-50">
                            <span className="text-sm font-medium text-gray-800 pr-4 truncate">{p.name}</span>
                            <span className="text-right text-sm font-bold text-indigo-600">₹{fmt(p.revenue)}</span>
                            <span className="text-right text-sm text-gray-600">{fmt(p.qty)}</span>
                            <span className="text-right"><GrowthBadge v={full?.revenueGrowth}/></span>
                          </div>
                        );
                      })}
                      {(catProdMap[cat.name]?.length || 0) > 12 && (
                        <p className="px-5 py-2 text-xs text-gray-400 border-t border-gray-50">+{catProdMap[cat.name].length - 12} more items — switch to Products tab to see all</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* PRODUCTS TABLE                                               */}
          {/* ════════════════════════════════════════════════════════════ */}
          {activeTab === "products" && (
            <div className="space-y-4">
              {/* Filter bar */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input type="text" placeholder="Search products..." value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={14} className="text-gray-400 flex-shrink-0"/>
                  <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}
                    className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="all">All Categories</option>
                    {catNames.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <span className="text-xs text-gray-400 self-center whitespace-nowrap">{filteredProducts.length} products</span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      {[
                        { key: null,           label: "#",        cls: "w-10" },
                        { key: "name",         label: "Product",  cls: "min-w-[160px]" },
                        { key: "category",     label: "Category", cls: "min-w-[120px]" },
                        { key: "qty",          label: "Units",    cls: "text-right" },
                        { key: "revenue",      label: "Revenue",  cls: "text-right" },
                        { key: "avgPrice",     label: "Avg Price",cls: "text-right" },
                        { key: "billFreqPct",  label: "Bill Freq",cls: "text-right" },
                        { key: "revenueShare", label: "Share",    cls: "text-right min-w-[90px]" },
                        { key: "revenueGrowth",label: "Growth",   cls: "text-right" },
                      ].map(col => (
                        <th key={col.label}
                          className={`px-4 py-3 text-xs font-black text-gray-400 uppercase tracking-wider ${col.cls} ${col.key ? "cursor-pointer select-none hover:text-gray-600" : ""}`}
                          onClick={() => col.key && handleSort(col.key)}>
                          {col.label}{col.key && <SortIcon col={col.key}/>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pageProducts.length === 0 ? (
                      <tr><td colSpan={9} className="py-16 text-center text-gray-300">
                        <ShoppingBag size={36} className="mx-auto mb-3 opacity-40"/>
                        <p>No products match your search</p>
                      </td></tr>
                    ) : pageProducts.map((p, idx) => {
                      const rank = (page - 1) * PAGE + idx + 1;
                      return (
                        <tr key={p.name} className="hover:bg-gray-50/60 transition">
                          <td className="px-4 py-3 text-xs text-gray-400">{rank}</td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-gray-900 text-sm">{p.name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{p.category}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-700">{fmt(p.qty)}</td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-indigo-600">₹{fmt(p.revenue)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">₹{fmt(p.avgPrice)}</td>
                          <td className="px-4 py-3 text-right text-xs text-gray-500">{p.billFreqPct}% <span className="text-gray-300">({p.billsAppeared})</span></td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 bg-gray-100 rounded-full">
                                <div className="h-1.5 rounded-full bg-indigo-400" style={{ width: `${Math.min(p.revenueShare, 100)}%` }}/>
                              </div>
                              <span className="text-xs text-gray-500 w-8 text-right">{p.revenueShare}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right"><GrowthBadge v={p.revenueGrowth}/></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-gray-500">
                    {(page-1)*PAGE+1}–{Math.min(page*PAGE, filteredProducts.length)} of {filteredProducts.length}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"><ChevronLeft size={16}/></button>
                    {Array.from({length: Math.min(totalPages,5)}, (_, i) => Math.max(1, Math.min(page-2,totalPages-4))+i).map(p2 => (
                      <button key={p2} onClick={()=>setPage(p2)} className={`w-8 h-8 rounded-lg text-sm font-medium transition ${page===p2?"bg-indigo-600 text-white":"hover:bg-gray-100 text-gray-600"}`}>{p2}</button>
                    ))}
                    <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"><ChevronRight size={16}/></button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════ */}
          {/* COMPARISON                                                   */}
          {/* ════════════════════════════════════════════════════════════ */}
          {activeTab === "comparison" && (
            <div className="space-y-8">
              {/* Metric toggle */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-gray-600">Compare by:</span>
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                  {[["revenue","Revenue"],["qty","Units Sold"]].map(([k,l]) => (
                    <button key={k} onClick={() => setCmpMetric(k)}
                      className={`px-4 py-1.5 rounded-lg text-xs font-bold transition ${cmpMetric===k?"bg-white shadow text-indigo-600":"text-gray-500 hover:text-gray-700"}`}>{l}</button>
                  ))}
                </div>
              </div>

              {/* Category comparison */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                  <Layers size={15} className="text-indigo-600"/>Category Comparison — Current vs Prior Period
                </h3>
                <div className="h-64">
                  <Bar data={cmpData} options={cmpBarOpts}/>
                </div>
              </div>

              {/* Product comparison */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                  <ShoppingBag size={15} className="text-teal-600"/>Top Products Comparison — Current vs Prior Period
                </h3>
                <div className="h-64">
                  <Bar data={prodCmpData} options={{ ...cmpBarOpts, datasets: { ...cmpBarOpts.datasets } }}/>
                </div>
              </div>

              {/* Table: category winners/losers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="rounded-xl border border-emerald-100 overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-2.5 text-xs font-black text-emerald-700 uppercase tracking-wider flex items-center gap-1.5"><TrendingUp size={12}/>Biggest Gainers (Category)</div>
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-50">
                      {[...categories].filter(c => c.revenueGrowth > 0).sort((a, b) => b.revenueGrowth - a.revenueGrowth).slice(0, 5).map(c => (
                        <tr key={c.name} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 text-sm font-semibold text-gray-800">{c.name}</td>
                          <td className="px-4 py-2.5 text-right text-xs text-gray-400">₹{fmt(c.priorRevenue)} → ₹{fmt(c.revenue)}</td>
                          <td className="px-4 py-2.5 text-right"><GrowthBadge v={c.revenueGrowth}/></td>
                        </tr>
                      ))}
                      {categories.filter(c => c.revenueGrowth > 0).length === 0 && <tr><td colSpan={3} className="px-4 py-4 text-center text-gray-300 text-sm">No gainers</td></tr>}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-xl border border-rose-100 overflow-hidden">
                  <div className="bg-rose-50 px-4 py-2.5 text-xs font-black text-rose-700 uppercase tracking-wider flex items-center gap-1.5"><TrendingDown size={12}/>Biggest Declines (Category)</div>
                  <table className="min-w-full">
                    <tbody className="divide-y divide-gray-50">
                      {[...categories].filter(c => c.revenueGrowth < 0).sort((a, b) => a.revenueGrowth - b.revenueGrowth).slice(0, 5).map(c => (
                        <tr key={c.name} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 text-sm font-semibold text-gray-800">{c.name}</td>
                          <td className="px-4 py-2.5 text-right text-xs text-gray-400">₹{fmt(c.priorRevenue)} → ₹{fmt(c.revenue)}</td>
                          <td className="px-4 py-2.5 text-right"><GrowthBadge v={c.revenueGrowth}/></td>
                        </tr>
                      ))}
                      {categories.filter(c => c.revenueGrowth < 0).length === 0 && <tr><td colSpan={3} className="px-4 py-4 text-center text-gray-300 text-sm">No declines</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Full comparison table */}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-3">All Products — Full Comparison</h3>
                <div className="overflow-x-auto rounded-xl border border-gray-100">
                  <table className="min-w-full">
                    <thead><tr className="bg-gray-50 text-left">
                      {["Product","Category","Cur Revenue","Prior Revenue","Rev Growth","Cur Units","Prior Units","Unit Growth"].map(h => (
                        <th key={h} className="px-4 py-3 text-xs font-black text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {products.slice(0, 25).map(p => (
                        <tr key={p.name} className="hover:bg-gray-50/60">
                          <td className="px-4 py-2.5 text-sm font-semibold text-gray-900">{p.name}</td>
                          <td className="px-4 py-2.5 text-xs text-gray-500">{p.category}</td>
                          <td className="px-4 py-2.5 text-sm font-bold text-indigo-600">₹{fmt(p.revenue)}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-500">₹{fmt(p.priorRevenue)}</td>
                          <td className="px-4 py-2.5"><GrowthBadge v={p.revenueGrowth}/></td>
                          <td className="px-4 py-2.5 text-sm text-gray-700">{fmt(p.qty)}</td>
                          <td className="px-4 py-2.5 text-sm text-gray-500">{fmt(p.priorQty)}</td>
                          <td className="px-4 py-2.5"><GrowthBadge v={p.qtyGrowth}/></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {products.length > 25 && <p className="px-4 py-2 text-xs text-gray-400 border-t border-gray-50">Showing top 25 — export CSV for full data</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductAnalytics;
